import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import InfoPage from '../InfoPage';
import ShowcaseUpload from '../../components/ShowcaseUpload';
import { getPropertyLabel, getPropertyLabelLower } from '../../utils/labelHelpers';
import './PropertyManagement.sass';

const PropertyCard = ({ property, onEdit, onDelete, onToggleStatus, onToggleFeatured, hostCategory }) => {
  const [showcaseAssets, setShowcaseAssets] = useState([]);
  const [showcaseLoaded, setShowcaseLoaded] = useState(false);

  useEffect(() => {
    if (!property?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const apiBase = window.location.hostname === 'localhost' ?
          'http://localhost:4000' : '';
        const res = await fetch(
          `${apiBase}/api/showcase?profile_kind=property&property_id=${encodeURIComponent(property.id)}`
        );
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && data?.ok) {
          setShowcaseAssets(Array.isArray(data.assets) ? data.assets : []);
        }
      } catch (_) {
        // best effort — leave empty
      } finally {
        if (!cancelled) setShowcaseLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [property?.id]);


  const statusColors = {
    active: '#4CAF50',
    draft: '#FFC107',
    inactive: '#9E9E9E',
    archived: '#607D8B'
  };
  
  const propertyLabel = getPropertyLabel(hostCategory);
  const propertiesLabelLower = getPropertyLabelLower(hostCategory, true);

  return (
    <div className="property-card">
      {property.featured && (
        <div className="property-card__featured-badge">Featured</div>
      )}
      
      <div className="property-card__image">
        {property.main_image_url || property.image_url ? (
          <img src={property.main_image_url || property.image_url} alt={property.name} />
        ) : (
          <div className="property-card__image-placeholder">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
              <circle cx="8.5" cy="8.5" r="1.5"/>
              <polyline points="21 15 16 10 5 21"/>
            </svg>
          </div>
        )}
      </div>

      <div className="property-card__content">
        <div className="property-card__header">
          <h3 className="property-card__title">{property.name || `Unnamed ${propertyLabel}`}</h3>
          <div 
            className="property-card__status"
            style={{ backgroundColor: statusColors[property.status || 'draft'] }}
          >
            {property.status || 'draft'}
          </div>
        </div>

        <div className="property-card__location">
          {(() => {
            const parts = [];
            if (property.location_city) parts.push(property.location_city);
            if (property.location_state) parts.push(property.location_state);
            if (property.location_country && property.location_country !== 'United States') {
              parts.push(property.location_country);
            }
            return parts.length ? parts.join(', ') : null;
          })() ? (
            (() => {
              const parts = [];
              if (property.location_city) parts.push(property.location_city);
              if (property.location_state) parts.push(property.location_state);
              if (property.location_country && property.location_country !== 'United States') {
                parts.push(property.location_country);
              }
              return parts.join(', ');
            })()
          ) : (
            <span className="property-card__no-location">No location set</span>
          )}
        </div>

        <div className="property-card__details">
          {property.bedrooms && (
            <span className="property-card__detail">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M2 20v-8a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v8"/>
                <path d="M4 10V6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v4"/>
                <path d="M12 4v6"/>
                <path d="M2 18h20"/>
              </svg>
              {property.bedrooms} bed{property.bedrooms !== 1 ? 's' : ''}
            </span>
          )}
          {property.bathrooms && (
            <span className="property-card__detail">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 4 8 6"/>
                <path d="M17 19v2"/>
                <path d="M2 12h20"/>
                <path d="M7 19v2"/>
                <path d="M9 5 7.621 3.621A2.121 2.121 0 0 0 4 5v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-5"/>
              </svg>
              {property.bathrooms} bath{property.bathrooms !== 1 ? 's' : ''}
            </span>
          )}
        </div>

        <div className="property-card__stats">
          <div className="property-card__stat">
            <span className="property-card__stat-value">{property.view_count || 0}</span>
            <span className="property-card__stat-label">Views</span>
          </div>
          <div className="property-card__stat">
            <span className="property-card__stat-value">{property.inquiry_count || 0}</span>
            <span className="property-card__stat-label">Inquiries</span>
          </div>
        </div>

        <div className="property-card__actions">
          <button 
            className="property-card__action property-card__action--edit"
            onClick={() => onEdit(property)}
          >
            Edit
          </button>
          {property.status === 'active' ? (
            <Tippy content={`By making a ${propertiesLabelLower.slice(0, -1)} inactive it just means you are not accepting collaborations at this time.`}>
              <button 
                className="property-card__action property-card__action--status"
                onClick={() => onToggleStatus(property)}
              >
                Deactivate
              </button>
            </Tippy>
          ) : (
            <button 
              className="property-card__action property-card__action--status"
              onClick={() => onToggleStatus(property)}
            >
              Activate
            </button>
          )}
          <button 
            className="property-card__action property-card__action--featured"
            onClick={() => onToggleFeatured(property)}
          >
            {property.featured ? '★' : '☆'}
          </button>
          <button 
            className="property-card__action property-card__action--delete"
            onClick={() => onDelete(property)}
          >
            Delete
          </button>
        </div>

        <div className="property-card__showcase">
          {showcaseLoaded ? (
            <ShowcaseUpload
              mode="dashboard"
              profileKind="property"
              profileId={property.id}
              value={showcaseAssets}
              onChange={setShowcaseAssets}
              heading={`${propertyLabel} showcase`}
              copy={`Add up to 3 photos or short-form reels of this ${propertiesLabelLower.slice(0, -1)}. Changes save automatically.`}
            />
          ) : (
            <div className="property-card__showcase-loading">Loading showcase…</div>
          )}
        </div>
      </div>
    </div>
  );
};

const PropertyManagement = () => {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [bulkOperation, setBulkOperation] = useState('');
  const [hostProfile, setHostProfile] = useState(null);
  const [propertyLimits, setPropertyLimits] = useState({
    currentCount: 0,
    maxProperties: 1,
    minProperties: 0,
    planName: 'Free',
    remainingProperties: 1
  });
  
  const email = typeof window !== 'undefined' ? 
    window.localStorage.getItem('postaway_email') || '' : '';

  // Add subscription check state
  const [authorized, setAuthorized] = useState(null);

  // Check subscription and host role before allowing access
  useEffect(() => {
    if (!email) {
      navigate('/');
      return;
    }

    (async () => {
      try {
        const res = await fetch('/api/access/subscription', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email })
        });
        const data = await res.json();

        if (res.ok && data.ok && data.active && (data.roles || []).includes('host')) {
          setAuthorized(true);
        } else {
          // Redirect to home if no active subscription or not a host
          navigate('/');
        }
      } catch (err) {
        // Redirect to home on error
        navigate('/');
      }
    })();
  }, [email, navigate]);

  // Fetch host profile
  const fetchHostProfile = useCallback(async () => {
    if (!email || authorized !== true) return;
    
    try {
      const apiBase = window.location.hostname === 'localhost' ? 
        'http://localhost:4000' : '';
      const response = await fetch(`${apiBase}/api/host/profile?email=${encodeURIComponent(email)}`);
      const data = await response.json();
      
      if (data.ok && data.profile) {
        setHostProfile(data.profile);
      }
    } catch (error) {
      console.error('Failed to fetch host profile:', error);
    }
  }, [email, authorized]);

  // Fetch properties
  const fetchProperties = useCallback(async () => {
    if (!email || authorized !== true) {
      return;
    }

    setLoading(true);
    try {
      const apiBase = window.location.hostname === 'localhost' ? 
        'http://localhost:4000' : '';
      
      const response = await fetch(`${apiBase}/api/properties/list?email=${encodeURIComponent(email)}`);
      const data = await response.json();
      
      if (data.ok) {
        setProperties(data.properties || []);
        // Update property limits
        if (data.propertyLimits) {
          setPropertyLimits(data.propertyLimits);
        }
      }
    } catch (error) {
      console.error('Failed to fetch properties:', error);
    } finally {
      setLoading(false);
    }
  }, [email, authorized]);

  useEffect(() => {
    if (authorized === true) {
      fetchHostProfile();
      fetchProperties();
    }
  }, [fetchHostProfile, fetchProperties, authorized]);

  // Handle property deletion
  const handleDelete = async (property) => {
    if (!window.confirm(`Are you sure you want to delete "${property.name}"?`)) {
      return;
    }

    try {
      const apiBase = window.location.hostname === 'localhost' ? 
        'http://localhost:4000' : '';
      
      const response = await fetch(`${apiBase}/api/properties/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, property_id: property.id })
      });

      const data = await response.json();
      if (data.ok) {
        setProperties(prev => prev.filter(p => p.id !== property.id));
      }
    } catch (error) {
      console.error('Failed to delete property:', error);
    }
  };

  // Handle status toggle
  const handleToggleStatus = async (property) => {
    const newStatus = property.status === 'active' ? 'inactive' : 'active';
    
    try {
      const apiBase = window.location.hostname === 'localhost' ? 
        'http://localhost:4000' : '';
      
      const response = await fetch(`${apiBase}/api/properties/update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email, 
          property_id: property.id,
          property: { status: newStatus }
        })
      });

      const data = await response.json();
      if (data.ok) {
        setProperties(prev => prev.map(p => 
          p.id === property.id ? { ...p, status: newStatus } : p
        ));
      }
    } catch (error) {
      console.error('Failed to update property status:', error);
    }
  };

  // Handle featured toggle
  const handleToggleFeatured = async (property) => {
    try {
      const apiBase = window.location.hostname === 'localhost' ? 
        'http://localhost:4000' : '';
      
      const response = await fetch(`${apiBase}/api/properties/update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email, 
          property_id: property.id,
          property: { featured: !property.featured }
        })
      });

      const data = await response.json();
      if (data.ok) {
        setProperties(prev => prev.map(p => 
          p.id === property.id ? { ...p, featured: !property.featured } : p
        ));
      }
    } catch (error) {
      console.error('Failed to update property featured status:', error);
    }
  };

  // Handle bulk operations
  const handleBulkOperation = async () => {
    if (selectedProperties.length === 0) {
      alert('Please select at least one property');
      return;
    }

    if (!bulkOperation) {
      alert('Please select a bulk operation');
      return;
    }

    try {
      const apiBase = window.location.hostname === 'localhost' ? 
        'http://localhost:4000' : '';
      
      const updates = bulkOperation === 'activate' ? { status: 'active' } :
                     bulkOperation === 'deactivate' ? { status: 'inactive' } :
                     bulkOperation === 'archive' ? { status: 'archived' } : {};
      
      const operation = bulkOperation === 'delete' ? 'delete' :
                       bulkOperation === 'toggle_featured' ? 'toggle_featured' :
                       'update_status';

      const response = await fetch(`${apiBase}/api/properties/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email,
          operation,
          property_ids: selectedProperties,
          updates
        })
      });

      const data = await response.json();
      if (data.ok) {
        fetchProperties();
        setSelectedProperties([]);
        setBulkOperation('');
      }
    } catch (error) {
      console.error('Failed to perform bulk operation:', error);
    }
  };

  // Filter properties
  const filteredProperties = properties.filter(property => {
    if (filter === 'all') return true;
    if (filter === 'featured') return property.featured;
    return property.status === filter;
  });

  const tempPropertyLabel = getPropertyLabel(hostProfile?.host_category);
  const tempPropertiesLabel = getPropertyLabel(hostProfile?.host_category, true);
  const tempPropertiesLabelLower = getPropertyLabelLower(hostProfile?.host_category, true);

  if (!email) {
    return (
      <InfoPage
        title={`${tempPropertyLabel} Management`}
        below={
          <div className="property-management-full-width">
            <div className="property-management">
              <div className="property-management__empty">
                <h2>Please sign in to manage your {tempPropertiesLabelLower}</h2>
                <button onClick={() => navigate('/')} className="btn btn--primary">
                  Go to Home
                </button>
              </div>
            </div>
          </div>
        }
        tallOverride={false}
        customMinHeight={400}
        disableStamps
      />
    );
  }

  const isAtLimit = propertyLimits.currentCount >= propertyLimits.maxProperties;
  const propertyLabel = getPropertyLabel(hostProfile?.host_category);
  const propertiesLabel = getPropertyLabel(hostProfile?.host_category, true);
  const propertiesLabelLower = getPropertyLabelLower(hostProfile?.host_category, true);
  
  const managementContent = (
    <div className="property-management-full-width">
      <div className="property-management">
      <div className="property-management__header">
        <h1 className="property-management__title">{propertyLabel} Management</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            fontSize: '14px',
            color: isAtLimit ? '#DC2626' : '#6B7280',
            fontWeight: '500'
          }}>
            {propertyLimits.currentCount}/{propertyLimits.maxProperties} {propertiesLabelLower}
          </div>
          <button 
            className="property-management__add-btn"
            onClick={() => {
              if (!isAtLimit) {
                navigate(`/property/editor?email=${encodeURIComponent(email)}`);
              }
            }}
            disabled={isAtLimit}
            title={isAtLimit ? `You've reached your ${propertiesLabelLower} limit of ${propertyLimits.maxProperties} ${propertiesLabelLower}. Upgrade to add more.` : ''}
            style={{
              opacity: isAtLimit ? 0.5 : 1,
              cursor: isAtLimit ? 'not-allowed' : 'pointer'
            }}
          >
            + Add {propertyLabel}
          </button>
        </div>
      </div>

      {properties.length > 0 && (
        <div className="property-management__toolbar">
          <div className="property-management__filters">
            <button 
              className={`filter-btn ${filter === 'all' ? 'filter-btn--active' : ''}`}
              onClick={() => setFilter('all')}
            >
              All ({properties.length})
            </button>
            <button 
              className={`filter-btn ${filter === 'active' ? 'filter-btn--active' : ''}`}
              onClick={() => setFilter('active')}
            >
              Active ({properties.filter(p => p.status === 'active').length})
            </button>
            <button 
              className={`filter-btn ${filter === 'draft' ? 'filter-btn--active' : ''}`}
              onClick={() => setFilter('draft')}
            >
              Draft ({properties.filter(p => p.status === 'draft').length})
            </button>
            <button 
              className={`filter-btn ${filter === 'inactive' ? 'filter-btn--active' : ''}`}
              onClick={() => setFilter('inactive')}
            >
              Inactive ({properties.filter(p => p.status === 'inactive').length})
            </button>
            <button 
              className={`filter-btn ${filter === 'featured' ? 'filter-btn--active' : ''}`}
              onClick={() => setFilter('featured')}
            >
              Featured ({properties.filter(p => p.featured).length})
            </button>
          </div>

          {selectedProperties.length > 0 && (
            <div className="property-management__bulk">
              <select 
                value={bulkOperation} 
                onChange={(e) => setBulkOperation(e.target.value)}
                className="property-management__bulk-select"
              >
                <option value="">Select action...</option>
                <option value="activate">Activate</option>
                <option value="deactivate">Deactivate</option>
                <option value="archive">Archive</option>
                <option value="toggle_featured">Toggle Featured</option>
                <option value="delete">Delete</option>
              </select>
              <button 
                className="property-management__bulk-btn"
                onClick={handleBulkOperation}
                disabled={!bulkOperation}
              >
                Apply to {selectedProperties.length} selected
              </button>
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div className="property-management__loading">Loading {propertiesLabelLower}...</div>
      ) : filteredProperties.length === 0 ? (
        <div className="property-management__empty">
          <svg className="property-management__empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/>
          </svg>
          <h2>No {propertiesLabelLower} found</h2>
          <p>Add your first {propertiesLabelLower.slice(0, -1)} to get started</p>
          <button 
            className="property-management__empty-btn"
            onClick={() => navigate(`/property/editor?email=${encodeURIComponent(email)}`)}
          >
            Add Your First {propertyLabel}
          </button>
        </div>
      ) : (
        <div className="property-management__grid">
          {filteredProperties.map(property => (
            <PropertyCard
              key={property.id}
              property={property}
              onEdit={(prop) => navigate(`/property/editor?email=${encodeURIComponent(email)}&id=${prop.id}`)}
              onDelete={handleDelete}
              onToggleStatus={handleToggleStatus}
              onToggleFeatured={handleToggleFeatured}
              hostCategory={hostProfile?.host_category}
            />
          ))}
        </div>
      )}
    </div>
    </div>
  );

  // Show loading while checking authorization
  if (authorized === null) {
    return (
      <InfoPage
        title={`${tempPropertyLabel} Management`}
        below={
          <div style={{ textAlign: 'center', fontFamily: "'Neue Einstellung', 'Poppins', sans-serif", color: '#0f084b' }}>
            Loading...
          </div>
        }
        disableStamps
        customMinHeight={600}
      />
    );
  }

  if (authorized === false) {
    return null; // navigate will handle redirect
  }

  return (
    <InfoPage
      title={`${propertyLabel} Management`}
      below={managementContent}
      tallOverride={false}
      customMinHeight={800}
      disableStamps
    />
  );
};

export default PropertyManagement;
